import { API_BASE } from './api-base';
import { clearReferral, loadReferral, referralToSpend } from './referral-capture';
import { TERMINAL_OUTCOMES, type ReferralOutcome } from './referral-outcome';

/**
 * Spends the invite stored at signup, now that the caller has a phone on their recipient.
 *
 * Never throws and never rethrows. It is called from the middle of the phone-verification success path,
 * where anything that escapes would surface to the user as a failed verification — of a verification that
 * has already succeeded. A referral is bookkeeping; it must not be able to break a signup.
 *
 * The stored id is dropped only on a TERMINAL outcome. Anything retryable is kept, so a referral is not
 * lost to the recipient's read-after-write lag or to a permission that has not been granted yet — the next
 * phone verification in that browser tries again.
 */
export async function claimStoredReferral(userId: string): Promise<ReferralOutcome | null> {
  try {
    const stored = loadReferral();
    const ref = referralToSpend({ stored, userId, now: Date.now() });
    if (!ref) {
      // Expired, absent, or captured for a different signed-in user: nothing to spend. An unusable
      // record is dropped so it cannot linger in a shared browser.
      if (stored) clearReferral();
      return null;
    }

    const res = await fetch(`${API_BASE}/api/referral-claim`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref }),
    });
    if (!res.ok) return 'retry';

    const { outcome } = (await res.json()) as { outcome?: ReferralOutcome };
    if (outcome && TERMINAL_OUTCOMES.has(outcome)) clearReferral();
    return outcome ?? 'failed';
  } catch {
    // Offline, blocked, or malformed answer — keep the id and try again next time.
    return 'retry';
  }
}
